import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import api, { CENTER_STORAGE_KEY } from '../api/axiosClient';
import { SorterCenterType } from '../types/sorter';

interface CenterContextType {
  centers: SorterCenterType[];
  selectedCenterId: number | null;
  selectCenter: (centerId: number) => Promise<void>;
}

const CenterContext = createContext<CenterContextType | undefined>(undefined);

export const useCenters = (): CenterContextType => {
  const context = useContext(CenterContext);
  if (!context) {
    throw new Error("useCenters must be used within a CenterProvider");
  }
  return context;
};

// The backend only trusts the integer-logistic-center-id header when the center
// belongs to the token's active context, so switch the context to the center's
// facility first and keep the new token.
const activateCenter = async (center: SorterCenterType) => {
  const response = await api.post(`/auth/switch-context`, {
    type: 'facility',
    id: center.facility_id,
  });
  localStorage.setItem("token", response.data.auth.accessToken);
  localStorage.setItem(CENTER_STORAGE_KEY, String(center.id));
};

// Loads the centers that have sorters and keeps the selected one in localStorage,
// where the axios interceptor reads it to send the integer-logistic-center-id header.
// Children are rendered only once the selection is settled, so their first requests
// already go out with the right center.
export const CenterProvider = ({ children }: { children: ReactNode }) => {
  const [centers, setCenters] = useState<SorterCenterType[]>([]);
  const [selectedCenterId, setSelectedCenterId] = useState<number | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const fetchCenters = async () => {
      try {
        const response = await api.get<SorterCenterType[]>(`/sorter/centers`);
        const data = response.data;
        setCenters(data);

        const storedId = Number(localStorage.getItem(CENTER_STORAGE_KEY));
        const initial = data.find((center) => center.id === storedId) ?? data[0];
        if (initial) {
          await activateCenter(initial);
          setSelectedCenterId(initial.id);
        } else {
          localStorage.removeItem(CENTER_STORAGE_KEY);
        }
      } catch (error) {
        console.error("Error fetching centers with sorters:", error);
        localStorage.removeItem(CENTER_STORAGE_KEY);
      } finally {
        setLoaded(true);
      }
    };
    fetchCenters();
  }, []);

  const selectCenter = async (centerId: number) => {
    const center = centers.find((c) => c.id === centerId);
    if (!center) return;
    try {
      await activateCenter(center);
      setSelectedCenterId(centerId);
    } catch (error) {
      console.error("Error switching to center:", error);
    }
  };

  if (!loaded) {
    return null;
  }

  return (
    <CenterContext.Provider value={{ centers, selectedCenterId, selectCenter }}>
      {children}
    </CenterContext.Provider>
  );
};
